import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DirectionsRun as RunIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { teamService } from '../../services/teamService';
import { isValidInvitationCode } from '../../utils/invitationUtils';

interface JoinTeamDialogProps {
  open: boolean;
  onClose: () => void;
  onTeamJoined: (teamId: string) => void; // Pass teamId to parent
}

const JoinTeamDialog: React.FC<JoinTeamDialogProps> = ({
  open,
  onClose,
  onTeamJoined,
}) => {
  const [invitationCode, setInvitationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('JoinTeamDialog: User:', user);
    console.log('JoinTeamDialog: User role:', user?.role);
    
    if (!user) {
      setError('You must be logged in to join a team');
      return;
    }

    // Allow athletes, coaches, and head coaches to join teams
    if (user.role !== 'athlete' && user.role !== 'coach' && user.role !== 'headCoach') {
      setError('Invalid user role');
      return;
    }

    if (!isValidInvitationCode(invitationCode)) {
      setError('Please enter a valid 6-character invitation code');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await teamService.joinTeamByCode(invitationCode, user.id);
      
      if (result.success && result.teamId) {
        setSuccess('Successfully joined the team!');
        setInvitationCode('');
        
        // Notify parent with teamId so it can update auth context
        onTeamJoined(result.teamId);
        
        setTimeout(() => {
          onClose();
          setSuccess('');
        }, 2000);
      } else {
        setError(result.error || 'Failed to join team');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to join team');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setInvitationCode('');
    setError('');
    setSuccess('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RunIcon />
            Join a Team
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            {user?.role === 'coach' || user?.role === 'headCoach' 
              ? 'Enter the invitation code to join a team as a coach.'
              : 'Enter the invitation code provided by your coach to join their team.'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Invitation Code"
            value={invitationCode}
            onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            margin="normal"
            required
            disabled={loading}
            inputProps={{
              maxLength: 6,
              style: { 
                textAlign: 'center',
                fontSize: '1.2rem',
                letterSpacing: 2,
                fontFamily: 'monospace'
              }
            }}
            helperText="Enter the 6-character code provided by your coach"
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !invitationCode.trim()}
          >
            {loading ? <CircularProgress size={20} /> : 'Join Team'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default JoinTeamDialog;
